/**
 * DocuSeal template configuration for the Broker MC Authority agreement.
 * Single source of truth for field names, roles, and types.
 *
 * Used by:
 * - Backend adapter/service to prefill field values
 * - Startup validation to verify the remote template matches expectations
 */

export const DOCUSEAL_ROLES = {
  MOTOR_CARRIER: 'Motor Carrier',
  TRANSPORTATION_BROKER: 'Transportation Broker',
} as const

export type DocuSealRole = (typeof DOCUSEAL_ROLES)[keyof typeof DOCUSEAL_ROLES]

export const DOCUSEAL_FIELDS = {
  // Motor Carrier (driver) fields
  CARRIER_NAME: { name: 'Carrier Name', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  CARRIER_ADDRESS: { name: 'Carrier Address', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  USDOT: { name: 'USDOT #', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  CARRIER_REPRESENTATIVE: {
    name: 'Representative Name',
    /**
     * The signing form titles a step with the field's template name unless the
     * envelope sends a title, so without it the signer is asked for a
     * "Representative Name" on a line the PDF labels Print Name (CARR-43).
     */
    title: 'Print Name',
    role: DOCUSEAL_ROLES.MOTOR_CARRIER,
    type: 'text',
  },
  CARRIER_MC_NUMBER: { name: 'MC #', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  /**
   * Prefilled from the `ein` column and from nothing else. The label says
   * "EIN/SSN" because the blank accepts either, but a driver's SSN lives
   * encrypted in `onboarding.drivers.ssn_encrypted` and is deliberately never
   * decrypted to fill a third-party document — a carrier with no EIN types
   * their own SSN at signing time.
   */
  CARRIER_EIN: { name: 'EIN/SSN', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  /**
   * The carrier's entity type, but Allied's to tick: carriers rarely know how
   * their business is legally classified, and nothing in the database records
   * it, so it is never prefilled (CARR-38). Declared so `validateDocuSealTemplate`
   * asserts it exists and stays on the broker's role.
   */
  CARRIER_ENTITY_TYPE: {
    name: 'Type of company',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'multiple',
  },
  /** Prefilled `Owner` and left editable: the signer may hold another office (CARR-43). */
  CARRIER_TITLE: { name: 'Title', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'text' },
  CARRIER_SIGNATURE: { name: 'Signature', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'signature' },
  CARRIER_DATE: { name: 'Date', role: DOCUSEAL_ROLES.MOTOR_CARRIER, type: 'date' },

  // Transportation Broker (Allied) fields
  /**
   * Opening "made and entered into this ___ day of ___" date.
   * Text (not date) field because DocuSeal's native date type doesn't
   * support the "13 day of April 2026" format. Prefilled at envelope
   * creation; the coordinator can correct it, like every broker field (CARR-43).
   */
  AGREEMENT_DATE: {
    name: 'Agreement Date',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'text',
  },
  BROKER_NAME: {
    name: 'Broker Name',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'text',
  },
  BROKER_ADDRESS: {
    name: 'Broker Address',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'text',
  },
  BROKER_REPRESENTATIVE: {
    name: 'Representative Name',
    title: 'Print Name',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'text',
  },
  BROKER_TITLE: { name: 'Title', role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER, type: 'text' },
  BROKER_SIGNATURE: {
    name: 'Signature',
    role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER,
    type: 'signature',
  },
  BROKER_DATE: { name: 'Date', role: DOCUSEAL_ROLES.TRANSPORTATION_BROKER, type: 'date' },
} as const

export type DocuSealFieldKey = keyof typeof DOCUSEAL_FIELDS

/** What the signature block's Title lines print unless the signer changes theirs (CARR-43). */
export const AGREEMENT_SIGNER_TITLES = {
  MOTOR_CARRIER: 'Owner',
  TRANSPORTATION_BROKER: 'Safety Coordinator',
} as const
